/**
 * This device's copy of a person's tour progress and per-app help settings, so a Skip or Done is never lost.
 *
 * localStorage key `ccms.help.v1.{userId}` holds the last merged records (server plus this device's unsent
 * changes) and the writes the server has not confirmed yet (one per key, the newest). Every storage access
 * is wrapped in try/catch: a private window or blocked storage just means the copy lives in memory for the
 * session. Pure functions only; HelpProvider owns the state, the server calls and the retries.
 *
 * The server stays the source of truth across devices: see mergeHelpRecords and dropOvertakenWrites.
 */

import { HelpTourProgressStatusIds, type HelpAppPreferenceView, type HelpProgressView } from '@actschurch/ccms-sdk';

const STORAGE_PREFIX = 'ccms.help.v1.';

export interface HelpProgressWrite {
  kind: 'progress';
  tourKey: string;
  version: number;
  statusID: number;
  /** When the change was made on this device (ISO). */
  at: string;
}

export interface HelpAppWrite {
  kind: 'app';
  appKey: string;
  autoStartTours: boolean;
  /** When the change was made on this device (ISO). */
  at: string;
}

export type HelpPendingWrite = HelpProgressWrite | HelpAppWrite;

export interface HelpLocalState {
  /** Tour progress by tourKey. */
  progress: Record<string, HelpProgressView>;
  /** Per-app help settings by appKey. */
  apps: Record<string, HelpAppPreferenceView>;
  /** Writes the server has not confirmed yet. */
  pending: HelpPendingWrite[];
}

export function emptyHelpLocalState(): HelpLocalState {
  return { progress: {}, apps: {}, pending: [] };
}

export function helpStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export const progressWriteKey = (tourKey: string) => `progress:${tourKey}`;
export const appWriteKey = (appKey: string) => `app:${appKey}`;

export function pendingKey(write: HelpPendingWrite): string {
  return write.kind === 'progress' ? progressWriteKey(write.tourKey) : appWriteKey(write.appKey);
}

/** Milliseconds for an ISO time; 0 when missing or unreadable (so a real time always wins). */
export function timeOf(value: string | null | undefined): number {
  if (!value) return 0;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : 0;
}

// ---------------------------------------------------------------- storage

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isKey = (v: unknown): v is string => typeof v === 'string' && v.length > 0;
const optionalString = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function readProgress(raw: unknown): Record<string, HelpProgressView> {
  const out: Record<string, HelpProgressView> = {};
  if (!isObject(raw)) return out;
  for (const [key, v] of Object.entries(raw)) {
    if (!isObject(v) || v.tourKey !== key || !isKey(key) || typeof v.version !== 'number' || typeof v.statusID !== 'number') continue;
    out[key] = { tourKey: key, version: v.version, statusID: v.statusID, statusName: optionalString(v.statusName), updatedAt: optionalString(v.updatedAt) };
  }
  return out;
}

function readApps(raw: unknown): Record<string, HelpAppPreferenceView> {
  const out: Record<string, HelpAppPreferenceView> = {};
  if (!isObject(raw)) return out;
  for (const [key, v] of Object.entries(raw)) {
    if (!isObject(v) || v.appKey !== key || !isKey(key) || typeof v.autoStartTours !== 'boolean') continue;
    out[key] = { appKey: key, autoStartTours: v.autoStartTours, updatedAt: optionalString(v.updatedAt) };
  }
  return out;
}

function readPending(raw: unknown): HelpPendingWrite[] {
  if (!Array.isArray(raw)) return [];
  const byKey = new Map<string, HelpPendingWrite>();
  for (const v of raw) {
    if (!isObject(v) || !isKey(v.at)) continue;
    let write: HelpPendingWrite | null = null;
    if (v.kind === 'progress' && isKey(v.tourKey) && typeof v.version === 'number' && typeof v.statusID === 'number') {
      write = { kind: 'progress', tourKey: v.tourKey, version: v.version, statusID: v.statusID, at: v.at };
    } else if (v.kind === 'app' && isKey(v.appKey) && typeof v.autoStartTours === 'boolean') {
      write = { kind: 'app', appKey: v.appKey, autoStartTours: v.autoStartTours, at: v.at };
    }
    if (!write) continue;
    const key = pendingKey(write);
    const seen = byKey.get(key);
    if (!seen || timeOf(write.at) >= timeOf(seen.at)) byKey.set(key, write);
  }
  return Array.from(byKey.values());
}

/** The stored copy for a person; null when there is none or storage cannot be read. */
export function readHelpLocal(userId: string): HelpLocalState | null {
  try {
    const raw = window.localStorage.getItem(helpStorageKey(userId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed)) return null;
    return { progress: readProgress(parsed.progress), apps: readApps(parsed.apps), pending: readPending(parsed.pending) };
  } catch {
    return null;
  }
}

/** Saves the copy; false when storage is not available (full, blocked or a private window). */
export function writeHelpLocal(userId: string, state: HelpLocalState): boolean {
  try {
    window.localStorage.setItem(helpStorageKey(userId), JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- merging

/**
 * Merges the server's records with this device's, per key:
 * - a key with an unsent write keeps this device's record (its newest change, on its way to the server);
 * - on both sides, the newer updatedAt wins (a tie goes to the server);
 * - a record only on this device without an unsent write is dropped: the server forgot it (for example the
 *   tours were reset on another device).
 * Without server data (not loaded) this device's records are returned as they are.
 */
export function mergeHelpRecords<T extends { updatedAt?: string | null }>(
  server: readonly T[] | undefined,
  keyOf: (item: T) => string,
  local: Record<string, T>,
  hasPending: (key: string) => boolean
): Record<string, T> {
  if (!server) return { ...local };
  const out: Record<string, T> = {};
  for (const item of server) {
    const key = keyOf(item);
    if (!key) continue;
    const mine = local[key];
    out[key] = mine && (hasPending(key) || timeOf(mine.updatedAt) > timeOf(item.updatedAt)) ? mine : item;
  }
  for (const [key, mine] of Object.entries(local)) {
    if (!(key in out) && hasPending(key)) out[key] = mine;
  }
  return out;
}

/** The progress map and per-app settings to use now: server and this device merged. */
export function mergedProgress(server: readonly HelpProgressView[] | undefined, state: HelpLocalState): Record<string, HelpProgressView> {
  const keys = new Set(state.pending.map(pendingKey));
  return mergeHelpRecords(server, (p) => p.tourKey, state.progress, (k) => keys.has(progressWriteKey(k)));
}

export function mergedApps(server: readonly HelpAppPreferenceView[] | undefined, state: HelpLocalState): Record<string, HelpAppPreferenceView> {
  const keys = new Set(state.pending.map(pendingKey));
  return mergeHelpRecords(server, (a) => a.appKey, state.apps, (k) => keys.has(appWriteKey(k)));
}

/** Rewrites the stored records as the merged view, for whichever server lists have loaded. */
export function reconcileHelpLocal(
  state: HelpLocalState,
  serverProgress: readonly HelpProgressView[] | undefined,
  serverApps: readonly HelpAppPreferenceView[] | undefined
): HelpLocalState {
  return {
    ...state,
    progress: serverProgress ? mergedProgress(serverProgress, state) : state.progress,
    apps: serverApps ? mergedApps(serverApps, state) : state.apps,
  };
}

/**
 * Run once when the server's progress and settings have loaded, before retrying: drops unsent writes the
 * server has since overtaken (another device changed the same key later), so an old change never overwrites
 * a newer one.
 */
export function dropOvertakenWrites(
  state: HelpLocalState,
  serverProgress: readonly HelpProgressView[],
  serverApps: readonly HelpAppPreferenceView[]
): HelpLocalState {
  const progress = new Map(serverProgress.map((p) => [p.tourKey, p] as const));
  const apps = new Map(serverApps.map((a) => [a.appKey, a] as const));
  const keep = state.pending.filter((w) => {
    const server = w.kind === 'progress' ? progress.get(w.tourKey) : apps.get(w.appKey);
    return !server || timeOf(server.updatedAt) <= timeOf(w.at);
  });
  return keep.length === state.pending.length ? state : { ...state, pending: keep };
}

// ---------------------------------------------------------------- changes

export function progressViewOf(write: HelpProgressWrite): HelpProgressView {
  return {
    tourKey: write.tourKey,
    version: write.version,
    statusID: write.statusID,
    statusName: write.statusID === HelpTourProgressStatusIds.Completed ? 'Completed' : 'Dismissed',
    updatedAt: write.at,
  };
}

export function appViewOf(write: HelpAppWrite): HelpAppPreferenceView {
  return { appKey: write.appKey, autoStartTours: write.autoStartTours, updatedAt: write.at };
}

/** A change made on this device: its record is updated now and the write queued (replacing an older one for the key). */
export function queueHelpWrite(state: HelpLocalState, write: HelpPendingWrite): HelpLocalState {
  const key = pendingKey(write);
  const pending = [...state.pending.filter((p) => pendingKey(p) !== key), write];
  return write.kind === 'progress'
    ? { ...state, pending, progress: { ...state.progress, [write.tourKey]: progressViewOf(write) } }
    : { ...state, pending, apps: { ...state.apps, [write.appKey]: appViewOf(write) } };
}

/** The server saved a write: keep its view and clear the queued write, unless a newer change replaced it meanwhile. */
export function confirmProgressWrite(state: HelpLocalState, write: HelpProgressWrite, saved: HelpProgressView): HelpLocalState {
  const key = pendingKey(write);
  const queued = state.pending.find((p) => pendingKey(p) === key);
  if (queued && queued.at !== write.at) return state;
  return { ...state, pending: state.pending.filter((p) => pendingKey(p) !== key), progress: { ...state.progress, [write.tourKey]: saved } };
}

export function confirmAppWrite(state: HelpLocalState, write: HelpAppWrite, saved: HelpAppPreferenceView): HelpLocalState {
  const key = pendingKey(write);
  const queued = state.pending.find((p) => pendingKey(p) === key);
  if (queued && queued.at !== write.at) return state;
  return { ...state, pending: state.pending.filter((p) => pendingKey(p) !== key), apps: { ...state.apps, [write.appKey]: saved } };
}

/** Forgets a queued write the server refused for good (for example the tour was deleted). */
export function dropHelpWrite(state: HelpLocalState, write: HelpPendingWrite): HelpLocalState {
  const key = pendingKey(write);
  const pending = state.pending.filter((p) => !(pendingKey(p) === key && p.at === write.at));
  return pending.length === state.pending.length ? state : { ...state, pending };
}

/** Reset: forgets these tours' records and queued writes on this device. */
export function forgetHelpTours(state: HelpLocalState, tourKeys: ReadonlySet<string>): HelpLocalState {
  const progress = { ...state.progress };
  tourKeys.forEach((k) => delete progress[k]);
  const pending = state.pending.filter((p) => !(p.kind === 'progress' && tourKeys.has(p.tourKey)));
  return { ...state, progress, pending };
}

/** Adds another copy's queued writes (newest per key wins), e.g. changes made before the user id was known. */
export function combineHelpLocal(base: HelpLocalState, extra: HelpLocalState): HelpLocalState {
  let out = base;
  for (const write of extra.pending) {
    const existing = out.pending.find((p) => pendingKey(p) === pendingKey(write));
    if (!existing || timeOf(write.at) > timeOf(existing.at)) out = queueHelpWrite(out, write);
  }
  return out;
}
